
import {
	ConfirmResult,
	createPopups,
	Deferred,
	ModalTitle,
	type AlertPopup,
	type Component,
	type ConfirmPopup,
	type Popup,
	type PopupsComposable,
} from "vue-utils"
import type { JSX } from "vue/jsx-runtime"
import type { BootstrapButtonColorType } from "../components/BootstrapButton"
import AlertForm from "../components/popups/AlertForm"
import ConfirmForm from "../components/popups/ConfirmForm"
import DangerForm from "../components/popups/DangerForm"

export { ConfirmResult }
export type { AlertPopup, ConfirmPopup, Popup }

export interface MISPopups extends PopupsComposable {
	showMessage(title: string, content: JSX.Element | string): Promise<void>
	showDangerPopup(options: DangerPopup): Promise<boolean>

	confirmDelete(objectType: string): Promise<boolean>
	confirmDelete(options: ConfirmDeleteOptions): Promise<boolean>
	confirmDelete(options: string | ConfirmDeleteOptions): Promise<boolean>
}

export interface DangerPopup extends Omit<Popup, "content"> {
	content: Component
	buttonColor?: BootstrapButtonColorType
	confirmText: string
	confirmIcon?: string

	cancelText?: string
}

export interface ConfirmDeleteOptions {
	title: string
	content: Component
}

function initPopups(): MISPopups {
	const defaults = createPopups({
		AlertPopup: AlertForm,
		ConfirmPopup: ConfirmForm,
	})

	async function showMessage(title: string, content: JSX.Element | string) {
		return await defaults.showAlertPopup(() => (
			<>
				<ModalTitle title={title} />
				<hr class="w-100" />
				{content}
			</>
		))
	}

	/**
	 * Opens a danger prompt
	 * @param options Either an object containing the popup options, or a functional component containing the popup content
	 * @returns A promise which will resolves to true if the accepts the positive action or false otherwise
	 */
	async function showDangerPopup(options: DangerPopup): Promise<boolean> {
		const deferred = new Deferred<boolean>()
		defaults.openPopup({
			...options,
			content: (popupProps) => (
				<DangerForm
					buttonColor={options.buttonColor}
					confirmText={options.confirmText}
					confirmIcon={options.confirmIcon}
					cancelText={options.cancelText}
					onCancel={() => {
						popupProps.close()
						deferred.resolve(false)
					}}
					onConfirm={() => {
						popupProps.close()
						deferred.resolve(true)
					}}
				>
					<options.content />
				</DangerForm>
			),
		})
		return deferred
	}

	async function confirmDelete(objectType: string): Promise<boolean>
	async function confirmDelete(options: ConfirmDeleteOptions): Promise<boolean>
	async function confirmDelete(options: string | ConfirmDeleteOptions): Promise<boolean> {
		if (typeof options === "string") {
			return await confirmDelete({
				title: `Delete ${options}?`,
				content: () => <>Are you sure you would like to delete this {options.toLowerCase()}?</>,
			})
		}

		return await showDangerPopup({
			confirmText: "Delete",
			confirmIcon: "fas fa-trash-alt",
			content: () => (
				<>
					<ModalTitle title={options.title} />
					<hr class="w-100" />
					<options.content />
				</>
			),
		})
	}

	return {
		...defaults,
		showMessage,
		confirmDelete,
		showDangerPopup,
	}
}

const popups = initPopups()
export function usePopups(): MISPopups {
	return popups
}
